import type { CSSProperties } from 'react';
import type { LayoutGraphicElement } from './layoutTypes';
import { layoutGraphicRenderPaths } from './layoutGraphicRenderGeometry';

export interface LayoutGraphicProps {
  readonly element: LayoutGraphicElement;
  readonly preview?: boolean;
}

function pathData(element: LayoutGraphicElement): string[] {
  return layoutGraphicRenderPaths(element).map((path) => {
    if (!path.points.length) return '';
    return `M${path.points.map((point) => `${point.x * 100} ${point.y * 100}`).join(' L')}${path.closed ? ' Z' : ''}`;
  });
}

function marker(element: Extract<LayoutGraphicElement, { category: 'point' }>) {
  const radius = Math.max(2, Math.min(48, element.marker.sizePt * 1.8));
  const low = 50 - radius;
  const high = 50 + radius;
  const common = { cx: 50, cy: 50, r: radius };
  if (element.marker.shape === 'square') {
    return <rect x={low} y={low} width={radius * 2} height={radius * 2} />;
  }
  if (element.marker.shape === 'diamond') {
    return <polygon points={`50,${low} ${high},50 50,${high} ${low},50`} />;
  }
  if (element.marker.shape === 'triangle') {
    return <polygon points={`50,${low} ${high},${high} ${low},${high}`} />;
  }
  const scale = radius / 18;
  const transform = `translate(50 50) scale(${scale}) translate(-50 -50)`;
  if (element.marker.shape === 'cross') {
    return <path transform={transform} d="M45 28H55V45H72V55H55V72H45V55H28V45H45Z" />;
  }
  if (element.marker.shape === 'x') {
    return <path transform={transform} d="M31 38L38 31L50 43L62 31L69 38L57 50L69 62L62 69L50 57L38 69L31 62L43 50Z" />;
  }
  return <circle {...common} />;
}

export function LayoutGraphic({ element, preview = false }: LayoutGraphicProps) {
  const stroke = 'stroke' in element ? element.stroke : element.marker.stroke;
  const fill = 'fill' in element ? element.fill : element.category === 'point' ? element.marker.fill : null;
  const dash = stroke.style === 'dash' ? '6 4' : stroke.style === 'dot' ? '1 3'
    : stroke.style === 'dashDot' ? '6 3 1 3' : undefined;
  const textStyle: CSSProperties | undefined = element.category === 'text' ? {
    color: element.textSymbol.color,
    fontFamily: element.textSymbol.fontFamily,
    fontSize: `${element.textSymbol.sizePt}pt`,
    fontStyle: element.textSymbol.fontStyle.includes('italic') ? 'italic' : 'normal',
    fontWeight: element.textSymbol.fontStyle.includes('bold') ? 'bold' : 'normal',
    textAlign: element.textSymbol.horizontalAlignment === 'justify'
      ? 'justify' : element.textSymbol.horizontalAlignment,
    opacity: element.textSymbol.opacityPercent / 100,
  } : undefined;
  const paths = pathData(element);
  const arrowStart = element.category === 'arrow' && element.arrowheads.start !== 'none';
  const arrowEnd = element.category === 'arrow' && element.arrowheads.end !== 'none';
  const arrowSize = element.category === 'arrow'
    ? Math.max(2, Math.min(30, element.arrowheads.sizePt))
    : 7;
  const markerId = `layout-arrow-${element.id.replace(/[^a-zA-Z0-9_-]/g, '')}`;

  return (
    <div
      data-testid={preview ? 'layout-graphic-preview' : `layout-graphic-${element.graphicType}`}
      data-graphic-type={element.graphicType}
      style={{
        width: '100%', height: '100%', position: 'relative',
        transform: `rotate(${element.rotation}deg) scale(${element.flipX ? -1 : 1}, ${element.flipY ? -1 : 1})`,
        transformOrigin: 'center',
        opacity: element.visible ? 1 : 0,
      }}
    >
      <svg aria-hidden viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
        <defs>
          <marker id={markerId} viewBox="0 0 10 10" refX="9" refY="5" markerWidth={arrowSize} markerHeight={arrowSize} orient="auto-start-reverse">
            <path d="M0 0L10 5L0 10Z" fill={fill?.color ?? stroke.color ?? 'black'} />
          </marker>
        </defs>
        <g
          fill={fill?.color ?? 'none'}
          fillOpacity={(fill?.opacityPercent ?? 100) / 100}
          fillRule={fill?.rule}
          stroke={stroke.color ?? 'none'}
          strokeOpacity={stroke.opacityPercent / 100}
          strokeWidth={Math.max(0.5, stroke.widthPt)}
          strokeDasharray={dash}
          strokeLinecap={stroke.cap}
          strokeLinejoin={stroke.join}
          vectorEffect="non-scaling-stroke"
          markerStart={arrowStart ? `url(#${markerId})` : undefined}
          markerEnd={arrowEnd ? `url(#${markerId})` : undefined}
        >
          {element.category === 'point'
            ? marker(element)
            : paths.map((renderPath, index) => <path key={index} d={renderPath} />)}
        </g>
      </svg>
      {element.category === 'text' ? (
        <div style={{
          ...textStyle,
          position: 'absolute',
          inset: `${element.text.marginPt}pt`,
          display: element.text.columnCount > 1 ? 'block' : 'flex',
          columnCount: element.text.columnCount,
          columnGap: `${element.text.columnGapPt}pt`,
          alignItems: element.textSymbol.verticalAlignment === 'top' ? 'flex-start'
            : element.textSymbol.verticalAlignment === 'bottom' ? 'flex-end' : 'center',
          justifyContent: element.textSymbol.horizontalAlignment === 'right' ? 'flex-end'
            : element.textSymbol.horizontalAlignment === 'left' ? 'flex-start' : 'center',
          overflow: element.text.overflow === 'visible' ? 'visible' : 'hidden',
          pointerEvents: 'none',
        }}>{element.text.text}</div>
      ) : null}
    </div>
  );
}
